import { useState, useEffect, useRef } from 'react';
import posthog from 'posthog-js';
import { supaFetch } from '../../lib/supabase.js';

// Safe localStorage read — returns [] on any parse/access failure.
function safeReadCart(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// ─── useCart ──────────────────────────────────────────────────────────────────
// Manages cart state with:
//   • Token-scoped localStorage key  →  prevents cross-customer pollution
//   • DB write-through (debounced 800ms) via save_cart SECURITY DEFINER RPC
//   • DB restore on mount via get_cart RPC  →  cross-device persistence
//
// Phase 8: Cart items carry an optional variant:
//   { _key, id, variant_id, variant_label, sku, name_en, name_ar,
//     qty, price, image, min_qty }
//
// @param vatRate   – multiplier for VAT price display
// @param showVat   – whether prices include VAT
// @param token     – catalogue token string (null for noprice/guest users)
//
// isSynced:
//   null      – waiting for initial DB fetch
//   true      – confirmed saved to DB (or no token → localStorage only)
//   false     – debounce pending / save in flight
//   undefined – no token, localStorage-only mode
export function useCart(vatRate = 0.14, showVat = false, token = null) {
  const STORAGE_KEY = token ? `sedra_cart_${token}` : 'sedra_cart_guest';

  // Initialise instantly from localStorage — zero loading delay for the user.
  const [cart, setCart] = useState(() => safeReadCart(STORAGE_KEY));
  const [isSynced, setIsSynced] = useState(token ? null : undefined);

  const syncTimerRef = useRef(null);
  const hasLoadedRef = useRef(false); // true after initial DB fetch completes

  // ── Write-through localStorage on every cart change ───────────────────────
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cart)); } catch {}
  }, [cart, STORAGE_KEY]);

  // ── On mount: fetch from DB and restore if localStorage is empty ──────────
  useEffect(() => {
    if (!token) { setIsSynced(undefined); return; }

    supaFetch('POST', 'rpc/get_cart', { p_token: token }, '', { 'Prefer': 'return=representation' })
      .then(({ data }) => {
        const dbItems = Array.isArray(data) ? data : [];
        if (dbItems.length > 0) {
          setCart(prev => {
            if (prev.length === 0) {
              try { localStorage.setItem(STORAGE_KEY, JSON.stringify(dbItems)); } catch {}
              return dbItems;
            }
            return prev; // local wins — customer was actively shopping
          });
        }
        hasLoadedRef.current = true;
        setIsSynced(true);
      })
      .catch(() => {
        hasLoadedRef.current = true;
        setIsSynced(true); // DB unavailable — fall back to localStorage only
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ── Debounced write-through to DB after every cart mutation ───────────────
  // Gated on hasLoadedRef so we never overwrite the DB with an empty cart
  // during the brief window before the initial fetch resolves.
  useEffect(() => {
    if (!token || !hasLoadedRef.current) return;

    setIsSynced(false);
    clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(async () => {
      try {
        await supaFetch('POST', 'rpc/save_cart', { p_token: token, p_items: cart }, '', { 'Prefer': 'return=minimal' });
        setIsSynced(true);
      } catch {
        setIsSynced(true); // avoid a stuck "Saving…" on network failure
      }
    }, 800);

    return () => clearTimeout(syncTimerRef.current);
  }, [cart, token]);

  // ── Mutations ─────────────────────────────────────────────────────────────

  const addToCart = (product, customQty, variant = null) => {
    const qtyToAdd = customQty || product.min_qty || 1;
    const cartKey  = `${product.id}:${variant?.id ?? ''}`;
    const effectivePrice =
      variant?.price_override != null ? variant.price_override : product.price;

    posthog.capture('cart_item_added', {
      product_id: product.id,
      category: product.category || 'uncategorized',
      quantity: qtyToAdd,
      has_variant: Boolean(variant),
      catalogue_access: token ? 'token' : 'guest',
    });

    setCart(prev => {
      const existing = prev.find(i => i._key === cartKey);
      if (existing) {
        return prev.map(i => i._key === cartKey ? { ...i, qty: i.qty + qtyToAdd } : i);
      }
      return [...prev, {
        _key: cartKey, id: product.id,
        variant_id: variant?.id ?? null, variant_label: variant?.label ?? null,
        sku: variant?.sku ?? product.sku,
        name_en: product.name_en, name_ar: product.name_ar,
        qty: qtyToAdd, price: effectivePrice,
        image: product.image, min_qty: product.min_qty || 1,
      }];
    });
  };

  const updateQty = (cartKey, qty) => {
    if (qty <= 0) {
      setCart(prev => prev.filter(i => i._key !== cartKey));
    } else {
      setCart(prev => prev.map(i => i._key === cartKey ? { ...i, qty } : i));
    }
  };

  const clearCart = () => setCart([]);

  // ── Derived values ────────────────────────────────────────────────────────

  const getPrice = (item) => {
    if (item.price === undefined || item.price === null) return 0;
    if (!showVat) return item.price;
    return Math.ceil(item.price * (1 + vatRate) / 5) * 5;
  };

  const cartTotal = cart.reduce((sum, item) => sum + getPrice(item) * item.qty, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);

  return { cart, addToCart, updateQty, clearCart, cartTotal, cartCount, getPrice, isSynced };
}
